import { Input, makeStyles, MenuItem, Select } from '@material-ui/core';
import { TRACKING_LOAD_STATUSES } from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import PriorityAssignConfirmation from './PriorityAssignConfirmation';
import PrioritySelectConfirmation from './PrioritySelectConfirmation';

type Props = {
  cell: {
    value: [number, number, boolean, number[]?];
  };
};

const PrioritySelect: React.FC<Props> = props => {
  const classes = useStyles();
  const { cell } = props;

  const {
    value: [priority, loadId, isDispatcherOrBroker, linkedLegs = []],
  } = cell;

  const { rootStore } = useStores();
  const [open, setOpen] = useState(false);
  const [openPriorityModal, setOpenPriorityModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [pendingPriority, setPendingPriority] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const applyPriorityUpdate = useCallback(
    async (newPriority: number) => {
      const load = rootStore.loadTableRows.get(`${loadId}`);
      if (load) {
        setLoading(true);
        const response = await load.updatePriority(newPriority);
        setLoading(false);
        if (response && !response.ok) {
          console.log('[DEBUG] Priority update error:', response.errors);
        }
      }
    },
    [loadId, rootStore],
  );

  const handleChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      event.stopPropagation();
      const newValue = event.target.value as number;
      if (newValue !== priority) {
        const load = rootStore.loadTableRows.get(`${loadId}`);
        if (load) {
          if (TRACKING_LOAD_STATUSES.includes(load.status)) {
            setOpenPriorityModal(true);
            setOpen(false);
            return;
          }

          // Trigger confirmation for all legs
          setPendingPriority(newValue);
          setOpenConfirmModal(true);
        }
      }

      setOpen(false);
    },
    [priority, loadId, rootStore],
  );

  const handleConfirm = useCallback(async () => {
    if (pendingPriority !== null) {
      await applyPriorityUpdate(pendingPriority);
    }
    setOpenConfirmModal(false);
    setPendingPriority(null);
  }, [pendingPriority, applyPriorityUpdate]);

  const handleCancel = useCallback(() => {
    setOpenConfirmModal(false);
    setPendingPriority(null);
  }, []);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isDispatcherOrBroker) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(true);
      }
    },
    [isDispatcherOrBroker],
  );

  const handleMouseDown = useCallback(() => {
    if (open) {
      setOpen(false);
    }
  }, [open]);

  return (
    <>
      <Select
        className={classes.select}
        fullWidth
        input={<Input disableUnderline />}
        onChange={handleChange}
        onClick={handleClick}
        value={priority}
        open={open}
        onMouseDown={handleMouseDown}
        disabled={!isDispatcherOrBroker}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((number: number) => (
          <MenuItem key={`${loadId}-${number}`} value={number}>
            {number}
          </MenuItem>
        ))}
      </Select>
      {!open && openPriorityModal && <PrioritySelectConfirmation />}
      {openConfirmModal && pendingPriority !== null && (
        <PriorityAssignConfirmation
          loading={loading}
          legId={loadId}
          legInfoList={linkedLegs}
          priority={pendingPriority}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};

const useStyles = makeStyles(() => ({
  select: {
    '& > .MuiSelect-select.MuiSelect-select': {
      backgroundColor: 'transparent',
      paddingRight: 0,
    },
  },
  popUpText: {
    fontSize: 12,
    padding: 5,
  },
}));

export default React.memo(observer(PrioritySelect));
